import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** One piece of the alert text: plain words, or a dollar amount that gets highlighted. */
export interface DneBannerSegment {
  text: string;
  amount: boolean;
}

const AMOUNT = /(\$\d[\d,]*(?:\.\d+)?)/g;

function formatMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * The minimum-markup DNE alert (VE-DNE). Shown up front wherever the admin reviews a vendor estimate that cannot
 * be approved because vendor amount + minimum profit is above the customer's revised DNE, so nobody has to click
 * Approve to find out. The words come from Job Ops (shown exactly, line by line); the three numbers (X, Y, Z) are
 * optional extras and are highlighted when present. The button is a request: the host opens its existing
 * create-customer-estimate dialog.
 *
 * Renders nothing when there is no message.
 */
@Component({
  selector: 'app-dne-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (lines().length) {
      <div
        class="dne-banner"
        [attr.role]="live() === 'alert' ? 'alert' : 'status'"
        [attr.aria-live]="live() === 'alert' ? 'assertive' : 'polite'"
        data-testid="dne-banner"
      >
        <span class="dne-banner__icon" aria-hidden="true">!</span>
        <div class="dne-banner__main">
          <div class="dne-banner__text" data-testid="dne-banner-text">
            @for (line of lines(); track $index; let first = $first) {
              <p class="dne-banner__line" [class.dne-banner__line--lead]="first">
                @for (seg of line; track $index) {<span [class.dne-banner__amt]="seg.amount">{{ seg.text }}</span>}
              </p>
            }
          </div>
          @if (hasNumbers()) {
            <dl class="dne-banner__stats" data-testid="dne-banner-stats">
              @if (requiredTotalText(); as x) {
                <div class="dne-banner__stat">
                  <dt>Vendor amount + minimum profit</dt>
                  <dd data-testid="dne-banner-x">{{ x }}</dd>
                </div>
              }
              @if (minimumProfitText(); as p) {
                <div class="dne-banner__stat dne-banner__stat--profit">
                  <dt>Minimum profit required</dt>
                  <dd data-testid="dne-banner-profit">{{ p }}</dd>
                  @if (markupText(); as m) {
                    <span class="dne-banner__stat-note" data-testid="dne-banner-markup">{{ m }} minimum markup</span>
                  }
                </div>
              }
              @if (customerDneText(); as y) {
                <div class="dne-banner__stat">
                  <dt>Customer DNE</dt>
                  <dd data-testid="dne-banner-y">{{ y }}</dd>
                </div>
              }
              @if (maxVendorText(); as z) {
                <div class="dne-banner__stat dne-banner__stat--goal">
                  <dt>Negotiate the vendor down to</dt>
                  <dd data-testid="dne-banner-z">{{ z }}</dd>
                </div>
              }
            </dl>
          }
        </div>
        @if (showAction()) {
          <button
            type="button"
            class="dne-banner__btn"
            data-testid="dne-banner-create"
            (click)="createCustomerEstimate.emit()"
          >
            {{ actionLabel() }}
          </button>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .dne-banner {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 10px 12px;
        box-sizing: border-box;
        width: 100%;
        padding: 10px 12px;
        background: var(--vb-danger-soft, #fef2f2);
        border: 1px solid var(--vb-danger-soft-border, #fca5a5);
        border-left: 6px solid var(--vb-danger, #b91c1c);
        border-radius: 8px;
        color: #7f1d1d;
        box-shadow: 0 1px 4px rgba(185, 28, 28, 0.18);
      }
      .dne-banner__icon {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        font-weight: 800;
        color: #fff;
        background: var(--vb-danger, #b91c1c);
      }
      .dne-banner__main {
        flex: 1 1 220px;
        min-width: 0;
        display: grid;
        gap: 8px;
      }
      .dne-banner__text {
        display: grid;
        gap: 3px;
      }
      .dne-banner__line {
        margin: 0;
        font-size: 12.5px;
        line-height: 1.45;
        overflow-wrap: anywhere;
      }
      .dne-banner__line--lead {
        font-size: 13px;
        font-weight: 700;
      }
      .dne-banner__amt {
        font-weight: 800;
        color: #991b1b;
        background: #fee2e2;
        border-radius: 3px;
        padding: 0 3px;
      }
      .dne-banner__stats {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin: 0;
      }
      .dne-banner__stat {
        flex: 1 1 110px;
        min-width: 0;
        padding: 6px 8px;
        background: #fff;
        border: 1px solid #fecaca;
        border-radius: 6px;
      }
      .dne-banner__stat dt {
        margin: 0;
        font-size: 10.5px;
        line-height: 1.3;
        color: #6b7280;
      }
      .dne-banner__stat dd {
        margin: 2px 0 0;
        font-size: 15px;
        font-weight: 800;
        color: #991b1b;
        font-variant-numeric: tabular-nums;
      }
      .dne-banner__stat--profit dd {
        color: #9a3412;
      }
      .dne-banner__stat-note {
        display: block;
        font-size: 11px;
        color: #7c2d12;
        opacity: 0.85;
      }
      .dne-banner__stat--goal {
        border-color: #86efac;
        background: #f0fdf4;
      }
      .dne-banner__stat--goal dd {
        color: #166534;
      }
      .dne-banner__btn {
        flex: 0 0 auto;
        align-self: center;
        font: inherit;
        font-size: 12.5px;
        font-weight: 700;
        padding: 8px 14px;
        border-radius: 6px;
        border: 1px solid var(--vb-danger, #b91c1c);
        background: var(--vb-danger, #b91c1c);
        color: #fff;
        cursor: pointer;
      }
      .dne-banner__btn:hover {
        filter: brightness(0.92);
      }
      .dne-banner__btn:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
      }
      @media (max-width: 480px) {
        .dne-banner__btn {
          width: 100%;
        }
      }
    `,
  ],
})
export class DneBannerComponent {
  /** The exact alert text from Job Ops (3 lines); blank = banner hidden. */
  readonly message = input<string | null | undefined>(null);
  /** X: vendor amount + minimum profit. */
  readonly requiredTotal = input<number | null | undefined>(null);
  /** Y: the customer's revised DNE. */
  readonly customerDne = input<number | null | undefined>(null);
  /** Z: the most the vendor can ask for and stay under the DNE. */
  readonly maxVendorAmount = input<number | null | undefined>(null);
  /** The minimum profit the customer's minimum markup asks for (X - vendor amount). */
  readonly minimumProfit = input<number | null | undefined>(null);
  /** The minimum markup % behind it. */
  readonly markupPercent = input<number | null | undefined>(null);
  /** The host hides the button when there is nothing for it to open (e.g. a customer estimate is already out). */
  readonly showAction = input(true);
  /** Button text; "Edit & resend to customer" when the customer sent the estimate back. */
  readonly actionLabel = input('Create customer estimate');
  /**
   * How screen readers hear it: 'alert' (assertive; the chat pop-up, where the admin is deciding on one request)
   * or 'status' (polite; the Estimates tab, where several banners sit on one page and re-check on every chooser flip).
   */
  readonly live = input<'alert' | 'status'>('alert');

  /** The admin pressed the action button. */
  readonly createCustomerEstimate = output<void>();

  /** The message split into lines, each into plain / amount pieces. */
  readonly lines = computed<DneBannerSegment[][]>(() => {
    const text = (this.message() ?? '').trim();
    if (!text) return [];
    return text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .map((l) =>
        l
          .split(AMOUNT)
          .filter((p) => p.length > 0)
          .map((p) => ({ text: p, amount: /^\$\d/.test(p) })),
      );
  });

  readonly requiredTotalText = computed(() => this.money(this.requiredTotal()));
  readonly customerDneText = computed(() => this.money(this.customerDne()));
  readonly maxVendorText = computed(() => this.money(this.maxVendorAmount()));
  readonly minimumProfitText = computed(() => this.money(this.minimumProfit()));
  readonly markupText = computed(() => {
    const m = this.markupPercent();
    return typeof m === 'number' && Number.isFinite(m) && m > 0 ? `${Math.round(m * 100) / 100}%` : null;
  });
  readonly hasNumbers = computed(
    () => !!(this.requiredTotalText() || this.minimumProfitText() || this.customerDneText() || this.maxVendorText()),
  );

  private money(n: number | null | undefined): string | null {
    return typeof n === 'number' && Number.isFinite(n) ? formatMoney(n) : null;
  }
}
